import { useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { Label } from 'shared/components/ui/label'
import { Textarea } from 'shared/components/ui/textarea'
import { useTranslate } from 'shared/lib/i18n'

/**
 * «Повреждена» — recording a Parcel damaged (Shohrux Р4, spec §18; ticket 42): the
 * hub worker, the driver or the Point's operator, where the Parcel is with them. A
 * comment is required, so «Отметить повреждение» waits for it. The mark is never cleared, and the
 * partner is told. A refusal is the server's, in words.
 *
 * `damage` is injected so the behaviour is tested without a backend.
 */
export type DamageRequest = RouterInputs['markers']['Damage']
export type DamageAnswer = RouterOutputs['markers']['Damage']

const damageViaTrpc = (input: DamageRequest) => trpcClient.markers.Damage.mutate(input)

export interface DamageActProps {
	parcelId: string
	/** The Parcel's code, as the act names it. */
	code: string
	damage?: (input: DamageRequest) => Promise<DamageAnswer>
	onDone?: (answer: Extract<DamageAnswer, { outcome: 'recorded' }>) => void
	onCancel?: () => void
	/** Without its own frame and title — when a dialog around it gives both. */
	bare?: boolean
}

export function DamageAct({ parcelId, code, damage = damageViaTrpc, onDone, onCancel, bare = false }: DamageActProps) {
	const { t } = useTranslate()
	const [comment, setComment] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [done, setDone] = useState(false)
	const ready = comment.trim() !== '' && !busy && !done

	const submit = async () => {
		if (!ready) return
		setBusy(true)
		setError(null)
		try {
			const answer = await damage({ parcelId, comment: comment.trim() })
			if (answer.outcome === 'refused') setError(t(`returnRefusal.${answer.reason}`))
			else {
				setDone(true)
				onDone?.(answer)
			}
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e))
		} finally {
			setBusy(false)
		}
	}

	return (
		<div
			className={bare ? 'flex flex-col gap-3' : 'flex flex-col gap-3 rounded-lg border-2 border-amber-500/60 p-3'}
			data-testid='damage-act'
		>
			{!bare && <div className='font-medium'>{t('damage.title', { code })}</div>}
			{done ? (
				<p className='text-sm' role='status' data-testid='damage-done'>
					{t('damage.done')}
				</p>
			) : (
				<>
					<div className='grid gap-1.5'>
						<Label htmlFor={`damage-comment-${parcelId}`}>{t('damage.comment')}</Label>
						<Textarea
							id={`damage-comment-${parcelId}`}
							data-testid='damage-comment'
							value={comment}
							onChange={(e) => setComment(e.target.value)}
							placeholder={t('damage.commentPlaceholder')}
						/>
					</div>
					{error && (
						<p role='alert' className='text-sm text-destructive' data-testid='damage-error'>
							{error}
						</p>
					)}
					<div className='flex gap-2'>
						<Button data-testid='damage-submit' disabled={!ready} onClick={submit}>
							{t('damage.submit')}
						</Button>
						{onCancel && (
							<Button variant='ghost' data-testid='damage-cancel' onClick={onCancel}>
								{t('damage.cancel')}
							</Button>
						)}
					</div>
				</>
			)}
		</div>
	)
}
